import { usePage } from "@inertiajs/react";
import { format } from "date-fns";
import { ChevronDownIcon, SearchIcon } from "lucide-react";
import {
	type ComponentProps,
	Fragment,
	useCallback,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import DotBadgeWithLabel from "@/components/shared/dot-badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { Package, PackageTotalPrice } from "@/types/admin-portal/package";
import type { GlobalPageProps } from "@/types/globals";

// labels live in brands.json under package_table.columns.<key>
const ALL_COLUMNS = [
	{ key: "package" },
	{ key: "status" },
	{ key: "visibility" },
	{ key: "createdAt" },
	{ key: "discount" },
	{ key: "pricePerVisit" },
	{ key: "totalPrice" },
	{ key: "serviceFee" },
	{ key: "adjustmentServiceFee" },
	{ key: "feePerVisit" },
	{ key: "totalFee" },
] as const;

type ColumnKey = (typeof ALL_COLUMNS)[number]["key"];

// SERVICE_FEE_ENABLE off: the backend bills neither fee, so the columns are not offered at all
const SERVICE_FEE_COLUMNS = ["serviceFee", "adjustmentServiceFee"] as const;

export interface PackageTableProps extends ComponentProps<"table"> {
	packages: Package[];
	totalPrices: PackageTotalPrice[];
}

export default function PackageTable({
	className,
	packages,
	totalPrices,
}: PackageTableProps) {
	const { t } = useTranslation("brands");
	const { locale, tzDate } = useDateContext();
	const serviceFeeEnabled =
		usePage<GlobalPageProps>().props.adminPortal?.featureFlags
			?.serviceFeeEnabled ?? false;
	const columns = useMemo(
		() =>
			ALL_COLUMNS.filter(
				(c) =>
					serviceFeeEnabled ||
					!(SERVICE_FEE_COLUMNS as readonly ColumnKey[]).includes(c.key),
			),
		[serviceFeeEnabled],
	);

	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<
		"all" | "active" | "inactive"
	>("all");
	const [visibilityFilter, setVisibilityFilter] = useState<
		"all" | "public" | "private"
	>("all");

	const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
		() => new Set(columns.map((c) => c.key)),
	);

	const toggleColumn = useCallback((key: ColumnKey) => {
		setVisibleColumns((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	const filteredPackages = useMemo(() => {
		return packages.filter((pkg) => {
			const matchSearch =
				search === "" || pkg.name.toLowerCase().includes(search.toLowerCase());
			const matchStatus =
				statusFilter === "all" ||
				(statusFilter === "active" && pkg.active) ||
				(statusFilter === "inactive" && !pkg.active);
			const matchVisibility =
				visibilityFilter === "all" ||
				(visibilityFilter === "public" && pkg.isPublic) ||
				(visibilityFilter === "private" && !pkg.isPublic);
			return matchSearch && matchStatus && matchVisibility;
		});
	}, [packages, search, statusFilter, visibilityFilter]);

	return (
		<div className="space-y-3">
			<div className="grid grid-cols-5 md:w-full gap-2">
				<div className="col-span-2">
					<Input
						StartIcon={{ icon: SearchIcon }}
						placeholder={t("package_table.search_placeholder")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="text-xs bg-input shadow-inner"
					/>
				</div>

				<Select
					value={statusFilter}
					onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
				>
					<SelectTrigger className="text-xs bg-input shadow-inner">
						<SelectValue placeholder={t("package_table.status")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t("package_table.all_status")}</SelectItem>
						<SelectItem value="active">{t("package_table.active")}</SelectItem>
						<SelectItem value="inactive">
							{t("package_table.inactive")}
						</SelectItem>
					</SelectContent>
				</Select>

				<Select
					value={visibilityFilter}
					onValueChange={(v) =>
						setVisibilityFilter(v as typeof visibilityFilter)
					}
				>
					<SelectTrigger className="text-xs bg-input shadow-inner">
						<SelectValue placeholder={t("package_table.visibility")} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">
							{t("package_table.all_visibility")}
						</SelectItem>
						<SelectItem value="public">{t("package_table.public")}</SelectItem>
						<SelectItem value="private">
							{t("package_table.private")}
						</SelectItem>
					</SelectContent>
				</Select>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="outline"
							size="sm"
							className="h-full bg-input shadow-inner w-full justify-start"
						>
							<span className="text-xs text-left text-muted-foreground/75">
								{t("package_table.columns_button")}
							</span>

							<ChevronDownIcon className="ml-auto h-4 w-4 text-muted-foreground/75" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
						<DropdownMenuLabel>
							{t("package_table.toggle_columns")}
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						{columns.map((col) => (
							<DropdownMenuCheckboxItem
								key={col.key}
								checked={visibleColumns.has(col.key)}
								onCheckedChange={() => toggleColumn(col.key)}
							>
								{t(`package_table.columns.${col.key}`)}
							</DropdownMenuCheckboxItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			<Table className={cn(className)}>
				<TableHeader>
					<TableRow>
						<TableHead colSpan={columns.length}>
							{packages.length && (
								<span className="text-nowrap text-muted-foreground font-light text-xs">
									{packages.length !== 0
										? t("package_table.found", {
												filtered: filteredPackages.length,
												total: packages.length,
											})
										: t("package_table.not_found")}
								</span>
							)}
						</TableHead>
					</TableRow>
					<TableRow>
						{visibleColumns.has("package") && (
							<TableHead>{t("package_table.columns.package")}</TableHead>
						)}
						{visibleColumns.has("status") && (
							<TableHead>{t("package_table.columns.status")}</TableHead>
						)}
						{visibleColumns.has("visibility") && (
							<TableHead>{t("package_table.columns.visibility")}</TableHead>
						)}
						{visibleColumns.has("createdAt") && (
							<TableHead>{t("package_table.columns.createdAt")}</TableHead>
						)}
						{visibleColumns.has("discount") && (
							<TableHead className="text-right">
								{t("package_table.columns.discount")}
							</TableHead>
						)}
						{visibleColumns.has("pricePerVisit") && (
							<TableHead className="text-right">
								{t("package_table.columns.pricePerVisit")}
							</TableHead>
						)}
						{visibleColumns.has("totalPrice") && (
							<TableHead className="text-right">
								{t("package_table.columns.totalPrice")}
							</TableHead>
						)}
						{visibleColumns.has("serviceFee") && (
							<TableHead className="text-right">
								{t("package_table.columns.serviceFee")}
							</TableHead>
						)}
						{visibleColumns.has("adjustmentServiceFee") && (
							<TableHead className="text-right">
								{t("package_table.columns.adjustmentServiceFee")}
							</TableHead>
						)}
						{visibleColumns.has("feePerVisit") && (
							<TableHead className="text-right">
								{t("package_table.columns.feePerVisit")}
							</TableHead>
						)}
						{visibleColumns.has("totalFee") && (
							<TableHead className="text-right">
								{t("package_table.columns.totalFee")}
							</TableHead>
						)}
					</TableRow>
				</TableHeader>

				<TableBody className="text-nowrap">
					{filteredPackages.map((packageItem) => (
						<Fragment key={packageItem.id}>
							<TableRow>
								{visibleColumns.has("package") && (
									<TableCell className="font-medium">
										<div className="flex flex-col items-start">
											<p>{packageItem.name}</p>
											<p className="font-light">
												{t("package_table.visits", {
													count: packageItem.numberOfVisit,
												})}
											</p>
										</div>
									</TableCell>
								)}
								{visibleColumns.has("status") && (
									<TableCell>
										<DotBadgeWithLabel
											variant={packageItem.active ? "success" : "destructive"}
										>
											<span>
												{packageItem.active
													? t("package_table.active")
													: t("package_table.inactive")}
											</span>
										</DotBadgeWithLabel>
									</TableCell>
								)}
								{visibleColumns.has("visibility") && (
									<TableCell>
										<DotBadgeWithLabel
											variant={packageItem.isPublic ? "default" : "secondary"}
										>
											<span>
												{packageItem.isPublic
													? t("package_table.public")
													: t("package_table.private")}
											</span>
										</DotBadgeWithLabel>
									</TableCell>
								)}
								{visibleColumns.has("createdAt") && (
									<TableCell className="text-muted-foreground/75">
										{format(packageItem.createdAt, "PP", {
											locale,
											in: tzDate,
										})}
									</TableCell>
								)}
								{visibleColumns.has("discount") && (
									<TableCell className="text-right">
										{packageItem.formattedDiscount}
									</TableCell>
								)}
								{visibleColumns.has("pricePerVisit") && (
									<TableCell className="text-right">
										{packageItem.formattedPricePerVisit}
									</TableCell>
								)}
								{visibleColumns.has("totalPrice") && (
									<TableCell className="font-medium text-right">
										{packageItem.formattedTotalPrice}
									</TableCell>
								)}
								{visibleColumns.has("serviceFee") && (
									<TableCell className="text-right">
										{packageItem.formattedServiceFee}
									</TableCell>
								)}
								{visibleColumns.has("adjustmentServiceFee") && (
									<TableCell className="text-right">
										{packageItem.formattedAdjustmentServiceFee}
									</TableCell>
								)}
								{visibleColumns.has("feePerVisit") && (
									<TableCell className="text-right">
										{packageItem.formattedFeePerVisit}
									</TableCell>
								)}
								{visibleColumns.has("totalFee") && (
									<TableCell className="font-medium text-right">
										{packageItem.formattedTotalFee}
									</TableCell>
								)}
							</TableRow>
						</Fragment>
					))}
				</TableBody>

				<TableFooter>
					<TableRow>
						<TableCell
							colSpan={
								(
									["package", "status", "visibility", "createdAt"] as const
								).filter((k) => visibleColumns.has(k)).length
							}
						>
							{t("package_table.total")}
						</TableCell>
						<TableCell
							colSpan={
								(["discount", "pricePerVisit", "totalPrice"] as const).filter(
									(k) => visibleColumns.has(k),
								).length
							}
							className="text-right"
						>
							<div className="flex flex-col space-y-0.5">
								{totalPrices.map((price) => (
									<span key={price.currency}>{price.formattedTotalPrice}</span>
								))}
							</div>
						</TableCell>
						{SERVICE_FEE_COLUMNS.some((k) => visibleColumns.has(k)) && (
							// fees are per package and not summed: an empty cell keeps the totals aligned
							<TableCell
								colSpan={
									SERVICE_FEE_COLUMNS.filter((k) => visibleColumns.has(k))
										.length
								}
							/>
						)}
						<TableCell
							colSpan={
								(["feePerVisit", "totalFee"] as const).filter((k) =>
									visibleColumns.has(k),
								).length
							}
							className="text-right"
						>
							<div className="flex flex-col space-y-0.5">
								{totalPrices.map((price) => (
									<span key={price.currency}>{price.formattedTotalFee}</span>
								))}
							</div>
						</TableCell>
					</TableRow>
				</TableFooter>
			</Table>
		</div>
	);
}
